import { type PortfolioProjectTextCoverStyle } from '#app/__generated__/api.js';
import { Link } from '#app/components/Link/index.js';
import { Text } from '#app/components/Primitives/Text.js';
import { ProjectCover } from '#app/components/ProjectCover/ProjectCover.js';
import { getRoute } from '@contra/routes';
import { css, styled } from 'styled-components';

const CARD_SIZE = (680 - 16 * 2) / 3;

const Card = styled.div<{ size?: number }>`
  ${({ size = CARD_SIZE, theme }) => css`
    display: block;
    flex: 0 0 ${size}px;
    padding: 16px;
    background-color: ${theme.colorsV2.white};
    border-radius: 10px;
    border: 1px solid ${theme.colorsV2.gray30};
  `}
`;

const Cover = styled.div`
  ${({ theme }) => css`
    margin-bottom: 16px;
    background-color: ${theme.colorsV2.gray05};
    border-radius: 8px;
  `}
`;

const Title = styled(Text).attrs({ as: 'h2', textStyle: 'subtitleSmall' })`
  ${({ theme }) => css`
    color: ${theme.colorsV2.black};
  `}
`;

const SkeletonText = styled.div<{ $width: string }>`
  ${({ $width, theme }) => css`
    margin-bottom: 8px;
    background-color: ${theme.colorsV2.gray05};
    border-radius: 8px;
    height: 12px;
    width: ${$width};
  `}
`;

type ProjectSquareCardProps = {
  readonly cardSize?: number;
  readonly coverImageUid?: string;
  readonly coverStyle?: PortfolioProjectTextCoverStyle;
  readonly onClick?: () => void;
  readonly shouldShowShadow?: boolean;
  readonly slug?: string;
  readonly title?: string;
};

const ProjectSquareCardBase = ({
  cardSize,
  coverImageUid,
  coverStyle,
  onClick,
  slug,
  title,
}: ProjectSquareCardProps): JSX.Element => {
  const CardBody = (
    <>
      <Cover>
        <ProjectCover
          image={coverImageUid ? { uid: coverImageUid } : undefined}
          imageAlt={title}
          textCoverStyle={coverStyle}
        />
      </Cover>
      {title ? (
        <Title>{title}</Title>
      ) : (
        <>
          <SkeletonText $width="90%" />
          <SkeletonText $width="70%" />
        </>
      )}
    </>
  );

  if (slug) {
    return (
      <Card
        as={Link}
        onClick={onClick}
        size={cardSize}
        to={getRoute('/p/@slug', { args: { slug } })}
      >
        {CardBody}
      </Card>
    );
  }

  return <Card size={cardSize}>{CardBody}</Card>;
};

const Container = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: no-wrap;
  overflow-x: auto;
  overflow-y: hidden;

  /** width, padding, and margin are this way to account for the box-shadow on cards (ie overflow scrollable container) */
  width: calc(100% + 32px);
  padding: 24px 16px;
  margin: -16px;

  & > * {
    margin-left: 16px;

    &:first-child {
      margin-left: 0;
    }
  }
`;

export const ProjectSquareCard = Object.assign(ProjectSquareCardBase, {
  Container,
  displayName: 'ProjectSquareCard',
});
